import Stripe from '%stripe%';
import invariant from 'invariant';

/**
 * Taken from/based on: https://github.com/erikras/react-redux-universal-hot-example/blob/master/src/utils/validation.js
 * These functions define validations to be run on redux-form fields
 * Usage example:
 *
 *    const validator = createValidator({
 *      firstName: required,
 *      lastName: required,
 *      middleInitial: isLength(1),
 *      npi: [required, isLength(10)],
 *      email: [required, validEmail],
 *      timeZoneName: required
 *    });
 *
 *    NewServicingProviderPage = reduxForm({
 *      form: 'NewServicingProviderPage',
 *      fields: ['prefix', 'firstName', 'lastName', 'email', 'middleInitial', 'suffix', 'attestationMark', 'npi', 'timeZoneName'],
 *      validate: validator
 *    }, mapStateToProps)(NewServicingProviderPage);
 *
 */
const isEmpty = value => value === undefined || value === null || value === '';
const join = rules => (value, data) =>
  rules.map(rule => rule(value, data)).filter(error => !!error)[0]; /* first error */

export function email(value) {
  if (!isEmpty(value) && !/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i.test(value)) {
    return 'Invalid email address';
  }
  return null;
}

export function customRequired(message = 'Required') {
  invariant(message, 'customRequired function requires an error message/text to display.');
  return value => {
    if (isEmpty(value)) {
      return message;
    }
    return null;
  };
}

export const required = customRequired('Required');

export function isLength(length) {
  return value => {
    if (!isEmpty(value) && value.length !== length) {
      return `Must be ${length} characters`;
    }
    return null;
  };
}

export function minLength(min) {
  return value => {
    if (!isEmpty(value) && value.length < min) {
      return `Must be at least ${min} characters`;
    }
    return null;
  };
}

export function maxLength(max) {
  return value => {
    if (!isEmpty(value) && value.length > max) {
      return `Must be no more than ${max} characters`;
    }
    return null;
  };
}

export function integer(value) {
  if (!Number.isInteger(Number(value))) {
    return 'Must be an integer';
  }
  return null;
}

export function oneOf(enumeration) {
  return value => {
    if (!~enumeration.indexOf(value)) {
      return `Must be one of: ${enumeration.join(', ')}`;
    }
    return null;
  };
}

export function match(field) {
  return (value, data) => {
    if (data) {
      if (value !== data[field]) {
        return 'Do not match';
      }
    }
    return null;
  };
}

export function validCVC(value) {
  return Stripe.card.validateCVC(value) ? null : 'is invalid';
}

export function validCC(value) {
  return Stripe.card.validateCardNumber(value) ? null : 'is invalid';
}

export function validCCExp(monthKey, yearKey) {
  return (value, allValues) => {
    if (allValues[yearKey] && allValues[monthKey]) {
      return Stripe.card.validateExpiry(allValues[monthKey], allValues[yearKey])
        ? null
        : 'month or year is invalid';
    }
    return null;
  };
}

export function validAMorPM(value) {
  return value.includes('_m') || value.includes('_M') ? 'please indicate AM or PM' : null;
}

export function createValidator(rules) {
  return (data = {}) => {
    const errors = {};
    Object.keys(rules).forEach(key => {
      const rule = join([].concat(rules[key])); // concat enables both functions and arrays of functions
      const error = rule(data[key], data);
      if (error) {
        errors[key] = error;
      }
    });
    return errors;
  };
}
